import {
  createContext,
  type Dispatch,
  type PropsWithChildren,
  type RefObject,
  type SetStateAction,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import { createPortal } from "react-dom";

type State = {
  x: number;
  y: number;
  visible: boolean;
  element?: HTMLElement;
  content: React.ReactNode;
  popoverElRef: RefObject<HTMLDivElement>;
};

const initialState: State = {
  x: 0,
  y: 0,
  visible: false,
  content: <></>,
  popoverElRef: null as any,
};

const PopoverContext = createContext({} as typeof initialState);
const PopoverDispatchContext = createContext<Dispatch<SetStateAction<State>>>(
  null as any
);

const PopoverProvider: React.FC<PropsWithChildren> = ({ children }) => {
  const elRef = useRef<HTMLDivElement>(null);
  const [state, setState] = useState({ ...initialState, popoverElRef: elRef });

  const close = useCallback(() => {
    setState((prev) => {
      if (!prev.visible) {
        return prev;
      }

      return { ...prev, visible: false };
    });
  }, []);

  const onDocumentScroll = useCallback((e: Event) => {
    if (!(e.target as HTMLElement).hasAttribute("data-popover")) {
      close();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (state.visible) {
      document.addEventListener("click", close, { once: true });
      document.body.addEventListener("scroll", onDocumentScroll, true);
      window.addEventListener("resize", close, { once: true });
    } else {
      document.body.removeEventListener("scroll", onDocumentScroll, true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.visible]);

  return (
    // @ts-ignore
    <PopoverDispatchContext.Provider value={setState}>
      {
        // @ts-ignore
      <PopoverContext.Provider value={state}>
        {children}
        {createPortal(
          <group
            data-popover=""
            data-scroll=""
            data-name="context"
            className={state.visible ? "open" : ""}
            ref={elRef}
            data-type="animate"
            data-radius="10"
            data-width="auto"
            data-space="5"
            data-background="context"
            data-elevation="1"
            style={{
              // @ts-ignore
              "--offset-top": state.y + "px", 
              zIndex: 999,
              position: "absolute",
              // transform:
              //   "translateX(" +
              //   (state.x - 10) +
              //   "px) translateY(" +
              //   (state.y - 5) +
              //   "px)",
              top: state.y + "px",
              left: state.x + "px",
              visibility: state.visible ? "visible" : "hidden",
            }}
          >
            {state.content}
          </group>,
          document.body
        )}
      </PopoverContext.Provider>
      }
    </PopoverDispatchContext.Provider>
  );
};

export const usePopover = () => {
  const dispatch = useContext(PopoverDispatchContext);

  const open = useCallback(
    ({
      x,
      y,
      content,
      element,
    }: {
      x?: number;
      y?: number;
      element?: HTMLElement;
      content: React.ReactElement;
    }) => {
      let isVisible = false;
      dispatch((prev) => {
        isVisible = prev.visible;
        return {
          ...prev,
          content,
        };
      });

      setTimeout(() => {
        dispatch((prev) => {
          if (element && element === prev.element && isVisible) {
            return { ...initialState, popoverElRef: prev.popoverElRef };
          }

          let anchorElBoundingClientRect: DOMRect | null = null;

          if (element) {
            anchorElBoundingClientRect = element.getBoundingClientRect();
            x = anchorElBoundingClientRect.x;
            y =
              anchorElBoundingClientRect.y + anchorElBoundingClientRect.height;
          }

          const popoverEl = prev.popoverElRef.current;

          if (popoverEl && x && y) {
            if (x + popoverEl.clientWidth > window.innerWidth) {
              if (element && anchorElBoundingClientRect) {
                x =
                  anchorElBoundingClientRect.x +
                  anchorElBoundingClientRect.width -
                  popoverEl.clientWidth;
              } else {
                x = window.innerWidth - popoverEl.clientWidth;
              }
            }

            if (y + popoverEl.clientHeight > window.innerHeight) {
              if (element && anchorElBoundingClientRect) {
                const newY =
                  anchorElBoundingClientRect.y - popoverEl.clientHeight;
                if (newY >= 0) {
                  y = newY;
                }
              } else {
                const newY = y - popoverEl.clientHeight;
                if (newY >= 0) {
                  y = newY;
                }
              }
            }

            return {
              ...prev,
              x: x,
              y: y,
              element: element,
              visible: true,
            };
          }

          return prev;
        });
      });
    },
    [dispatch]
  );

  const close = useCallback(() => {
    dispatch(initialState);
  }, [dispatch]);

  return { open, close };
};

export default PopoverProvider;
